/**
 * OrderDetail
 * --
 */
import React, { useState } from 'react';
import { ModalLayout, Title } from 'components';
import {
  Space,
  Tag,
  Spin,
  Select,
  Input,
  Button,
  Descriptions,
  Table,
  Divider,
  InputNumber,
  Flex,
  Radio,
} from 'antd';
import { useUpdateParcelServiceStatusBulk } from 'hooks/ParcelServiceHooks';
import { useUpdateShopOrder } from 'hooks/ShopOrderHooks';
import { openAddressModal } from 'utils/DaumAPIManager';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import trackDelivery from 'utils/TrackDelivery';
import Formatter from 'utils/Formatter';
import { encrypt } from 'utils/Crypto';
import {
  PAYMENT_METHOD_CONSTANT, // 주문 유형
  PAYMENT_STATUS_CONSTANT, // 주문의 결제상태
  PACKAGING_METHOD, // 포장방식
  DELIVERY_STATUS,
} from 'constants/ProductConstants';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import DeliveryCompany from 'constants/DeliveryCompany';
import Alert from 'utils/AlertManager';
import dayjs from 'dayjs';

const initialParcelInfo = {
  1: {},
};
const REFUND_STATUS = {
  PENDING: {
    label: '환불대기',
    title: '환불대기',
    color: 'blue',
  },
  APPROVED: {
    label: '환불승인',
    title: '환불승인',
    color: 'green',
  },
  REJECTED: {
    label: '환불반려',
    title: '환불반려',
    color: 'purple',
  },
  COMPLETED: {
    label: '환불완료',
    title: '환불완료',
    color: 'volcano',
  },
};
/**----------------------------------------
 * [Component] 필터
 * ----------------------------------------
 */
const RegularDetail = ({
  // onUpdateParcel,
  orderDetailData,
  onCancel,
  // type,
  type = 'shipping',
  onReload,
}) => {
  /* ===== MUTATE ===== */
  const { updateParcelServiceStatusBulk } = useUpdateParcelServiceStatusBulk();
  const { updateShopOrder } = useUpdateShopOrder();
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  // const [orderDetail, setOrderDetail] = useState(isModalVisible);
  // 택배정보 입력 데이터
  const [parcelInfoForm, setParcelInfoForm] = useState(initialParcelInfo);
  /* 환불관련 */
  const [refundModal, setRefundModal] = useState(null);
  const [refundPrice, setRefundPrice] = useState(0);
  const [refundReason, setRefundReason] = useState('');
  const [selectedRefundList, setSelectedRefundList] = useState([]);
  const [recoveryValue, setRecoveryValue] = useState({
    is_coupon_refund: 'Y',
    refund_point: 0,
  });

  //수정
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});
  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */

  /**--------------------
   * 주문정보 조회
   * --------------------
   */

  const orderDetail = React.useMemo(() => {
    const detailData = {
      ...orderDetailData,
      regular_payment_info: orderDetailData?.regular_payment_info?.toSorted(
        (a, b) => b.regular_payment_id - a.regular_payment_id,
      ),
    };
    //STATE
    const paymentList =
      orderDetailData?.regular_payment_info?.reduce((acc, item) => {
        acc[item.regular_payment_id] = item;
        return acc;
      }, {}) || {};

    setEditData({
      ...orderDetailData?.shop_order?.delivery_address,
      shop_order_id: orderDetailData?.shop_order_id,
    });
    setParcelInfoForm(paymentList);
    //DATA
    return detailData;
  }, [orderDetailData]);

  const paymentListColumns = (detail) => [
    {
      title: '회차',
      dataIndex: 'regular_payment_id',
      key: 'regular_payment_id',
      render: (regular_payment_id) => <p>{regular_payment_id}회차</p>,
      sorter: (a, b) => a.regular_payment_id - b.regular_payment_id,
    },
    {
      title: '주문번호',
      dataIndex: 'shop_order_id',
      key: 'shop_order_id',
      render: () => detail?.shop_order?.shop_order_id,
      sorter: (a, b) => a.shop_order_id - b.shop_order_id,
    },
    ...(detail
      ? [
          {
            title: '주문장배송비',
            dataIndex: 'final_delivery_price',
            key: 'final_delivery_price',
            render: (_, record) =>
              record.regular_payment_id === 1 ? (
                <p>
                  {stringToMoneyFormat(
                    detail?.shop_order?.final_delivery_price,
                  )}
                </p>
              ) : null,
            sorter: (a, b) => a.shop_order_id - b.shop_order_id,
          },
        ]
      : []),
    {
      title: '결제시리얼',
      dataIndex: 'payment_serial_number',
      key: 'payment_serial_number',
      render: () => detail?.shop_order?.payment_serial_number,
    },
    {
      title: '결제일(시간)',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (payment_date) =>
        dayjs.unix(payment_date).format('YYYY-MM-DD (HH:mm:ss)'),
      sorter: (a, b) => a.payment_date - b.payment_date,
    },
    {
      title: '결제상태',
      dataIndex: 'payment_state',
      key: 'payment_state',
      width: 100,
      render: (payment_state, record) => (
        <Select
          size="middle"
          style={{ width: 100 }}
          value={payment_state}
          options={Object.keys(PAYMENT_STATUS_CONSTANT).map((key) => ({
            label: PAYMENT_STATUS_CONSTANT[key].title,
            value: key,
          }))}
          onChange={(e) =>
            handleRegularBulkUpdateStatus(
              e,
              record?.regular_payment_id,
              'payment_state',
            )
          }
        />
      ),
      filters: Object.keys(PAYMENT_STATUS_CONSTANT).map((key) => ({
        text: PAYMENT_STATUS_CONSTANT[key].title,
        value: key,
      })),
      onFilter: (value, record) => record.payment_state === value,
    },
    {
      title: '결제수단',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 100,
      render: (payment_method) => (
        <Tag color={PAYMENT_METHOD_CONSTANT[payment_method]?.color}>
          {PAYMENT_METHOD_CONSTANT[payment_method]?.title}
        </Tag>
      ),
      filters: Object.keys(PAYMENT_METHOD_CONSTANT).map((key) => ({
        text: PAYMENT_METHOD_CONSTANT[key].title,
        value: key,
      })),
      onFilter: (value, record) => record.payment_method === value,
    },
    {
      title: '배송비',
      dataIndex: 'delivery_price',
      key: 'delivery_price',
      render: (delivery_price) => stringToMoneyFormat(delivery_price, '원'),
      sorter: (a, b) => a.delivery_price - b.delivery_price,
    },
    {
      title: '쿠폰/할인',
      dataIndex: 'discount_price',
      key: 'discount_price',
      render: (discount_price) => stringToMoneyFormat(discount_price, '원'),
      sorter: (a, b) => a.discount_price - b.discount_price,
    },
    {
      title: '총결제금액',
      dataIndex: 'price',
      key: 'price',
      render: (price) => stringToMoneyFormat(price, '원'),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '주문상태',
      dataIndex: 'purchase_status',
      key: 'purchase_status',
      width: 110,
      render: (purchase_status, record) => (
        <Select
          size="middle"
          style={{ width: 110 }}
          value={purchase_status}
          options={Object.keys(PURCHASE_STATUS).map((key) => ({
            label: PURCHASE_STATUS[key].title,
            value: key,
          }))}
          onChange={(e) =>
            handleRegularBulkUpdateStatus(
              e,
              record?.regular_payment_id,
              'purchase_status',
            )
          }
        />
        // <Tag color={PURCHASE_STATUS[purchase_status]?.color}>
        //   {PURCHASE_STATUS[purchase_status]?.title}
        // </Tag>
      ),
      filters: Object.keys(PURCHASE_STATUS).map((key) => ({
        text: PURCHASE_STATUS[key].title,
        value: key,
      })),
      onFilter: (value, record) => record.purchase_status === value,
    },
    {
      title: '결제정보',
      dataIndex: 'payment_info',
      key: 'payment_info',
      render: (payment_info) => (
        <>
          <li>- 결제 승인번호: {payment_info?.trno || payment_info?.tid}</li>
          <li>- 결제 금액: {payment_info?.amt || payment_info?.totalAmount}</li>
          <li>
            - 카드사: {payment_info?.msg1 || payment_info?.issuerCardName}
          </li>
          <li>
            - 결제시간:{payment_info?.trddt}-
            {dayjs(payment_info?.tradeDateTime).format('YYYY-MM-DD (HH:mm:ss)')}
          </li>
        </>
      ),
    },
  ];

  const deliveryListColumns = [
    {
      title: '회차',
      key: 'regular_payment_id',
      dataIndex: 'regular_payment_id',
      render: (regular_payment_id) => `${regular_payment_id}회차`,
    },
    {
      title: '택배사/운송장',
      key: 'delivery_company',
      dataIndex: 'delivery_company',
      render: (_, record) => (
        <Space.Compact style={{ width: '100%' }}>
          <Select
            style={{ width: 150 }}
            placeholder="택배사"
            options={Object.keys(DeliveryCompany).map((item) => ({
              value: DeliveryCompany[item].value,
              label: DeliveryCompany[item].label,
            }))}
            value={parcelInfoForm[record?.regular_payment_id]?.delivery_company}
            onChange={(e) =>
              handleChangeDelivery(
                'delivery_company',
                e,
                record?.regular_payment_id,
              )
            }
          />
          <Input
            placeholder="송장번호를 입력해주세요"
            style={{ width: 'calc(100% - 230px)' }}
            value={parcelInfoForm[record?.regular_payment_id]?.invoice_number}
            onChange={(e) =>
              handleChangeDelivery(
                'invoice_number',
                e.target.value,
                record?.regular_payment_id,
              )
            }
          />
          <Button
            type="primary"
            style={{ width: 80 }}
            onClick={() => handleSaveDeliveryInfo(record?.regular_payment_id)}
          >
            저장
          </Button>
        </Space.Compact>
      ),
    },
    {
      title: '배송상태',
      key: 'delivery_status',
      dataIndex: 'delivery_status',
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder="배송상태"
          options={Object.keys(DELIVERY_STATUS).map((item) => ({
            value: DELIVERY_STATUS[item].value,
            label: DELIVERY_STATUS[item].title,
          }))}
          value={parcelInfoForm[record?.regular_payment_id]?.delivery_status}
          onChange={(e) =>
            handleRegularBulkUpdateStatus(
              e,
              record?.regular_payment_id,
              'delivery_status',
            )
          }
        />
      ),
    },
    {
      title: '배송조회',
      key: 'recipient_address_detail',
      dataIndex: 'recipient_address_detail',
      key: 'recipient_address_detail',
      render: (_, record) => (
        <Button
          block
          disabled={
            !parcelInfoForm[record?.regular_payment_id]?.invoice_number
              ? true
              : false
          }
          onClick={() =>
            trackDelivery(
              parcelInfoForm[record?.regular_payment_id]?.delivery_company,
              parcelInfoForm[record?.regular_payment_id]?.invoice_number,
            )
          }
        >
          배송조회
        </Button>
      ),
    },
  ];

  const refundListColumns = (refundModal) => [
    {
      title: '상품명',
      dataIndex: 'product_name',
      width: 250,
      render: (_, item) => refundModal?.product?.product_name,
    },
    {
      title: '환불금액',
      dataIndex: 'refund_amount',
      width: 100,
      render: (refund_amount) => refund_amount,
    },
    {
      title: '환불수량',
      dataIndex: 'refund_num',
      width: 80,
      render: (refund_num) => refund_num,
    },
    {
      title: '환불상태',
      dataIndex: 'refund_status',
      width: 100,
      render: (refund_status) => (
        <Tag color={REFUND_STATUS[refund_status]?.color}>
          {REFUND_STATUS[refund_status]?.label}
        </Tag>
      ),
    },
    {
      title: '환불사유',
      dataIndex: 'refund_reason',
      width: 200,
      render: (refund_reason) => refund_reason,
    },
    {
      title: '환불일자',
      dataIndex: 'refund_date',
      width: 250,
      render: (refund_date) =>
        dayjs.unix(refund_date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '환불데이터',
      dataIndex: 'refund_data',
      width: 250,
      render: (refund_data) => (
        <ul style={{ fontSize: 12 }}>
          {(() => {
            const couponInfo = refund_data?.coupon_info;
            const currentCouponUserId =
              refundModal?.coupon_info?.coupon_user_id;
            const couponName = refundModal?.policy_coupon?.coupon_name;

            if (!couponInfo) return null;

            if (Array.isArray(couponInfo)) {
              return couponInfo.map((ReCoupon, idx) =>
                ReCoupon?.coupon_user_id === currentCouponUserId ? (
                  <li key={idx}>복구쿠폰 : {couponName}</li>
                ) : null,
              );
            }

            if (
              typeof couponInfo === 'object' &&
              couponInfo?.coupon_user_id === currentCouponUserId
            ) {
              return <li>복구쿠폰 : {couponName}</li>;
            }

            return null;
          })()}
          {/* 포인트 */}
          {(() => {
            const pointHistory = refund_data?.point_history;

            if (Array.isArray(pointHistory)) {
              const totalPoint = pointHistory.reduce(
                (acc, cur) => acc + (cur?.point || 0),
                0,
              );
              return <li>복구포인트 : {totalPoint} P</li>;
            } else if (pointHistory && typeof pointHistory === 'object') {
              return <li>복구포인트 : {pointHistory?.point || 0} P</li>;
            } else {
              return null;
            }
          })()}
        </ul>
      ),
    },
    {
      title: '환불결제정보',
      dataIndex: 'refund_payment_info',
      width: 250,
      render: (refund_payment_info) =>
        refund_payment_info ? (
          <ul style={{ fontSize: 12 }}>
            <li>- 취소 승인번호: {refund_payment_info?.tid} </li>
            <li>- 취소 금액: {refund_payment_info?.cancelAmount} </li>
            <li>- 카드사: {refund_payment_info?.issuerCardName} </li>
            <li>
              - 취소시간: {refund_payment_info?.tradeDateTime.slice(0, 8)}
              {'-'}
              {refund_payment_info?.tradeDateTime.slice(8, 14)}
            </li>
          </ul>
        ) : (
          <>{'-'}</>
        ),
    },
    {
      title: '환불현금영수증정보',
      dataIndex: 'refund_cash_bill_info',
      width: 250,
      render: (refund_cash_bill_info) =>
        refund_cash_bill_info ? (
          <ul style={{ fontSize: 12 }}>
            <li>- 취소 승인번호: {refund_cash_bill_info?.tid} </li>
            <li>- 취소 금액: {refund_cash_bill_info?.cancelAmount} </li>
            <li>
              - 메세지: {refund_cash_bill_info?.respMessage?.split('/')[0]}{' '}
            </li>
            <li>
              - 취소시간: {refund_cash_bill_info?.tradeDateTime.slice(0, 8)}
              {'-'}
              {refund_cash_bill_info?.tradeDateTime.slice(8, 14)}
            </li>
          </ul>
        ) : (
          <>{'-'}</>
        ),
    },
  ];

  const productColumns = [
    {
      title: '상품코드',
      key: 'product_code',
      dataIndex: 'product_code',
      width: 135,
    },
    {
      title: '상품명',
      key: 'product_name',
      dataIndex: 'product_name',
      width: 250,
      render: (v, record) => (
        <div>
          <a
            href={`/#/product/list/${record?.product_id}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(
                e.currentTarget.href,
                '_blank',
                'width=860, height=620',
              );
            }}
          >
            {v}
          </a>
          {/* <p>{JSON.stringify(record.product_option)}</p> */}
          <p
            style={{
              fontSize: '0.75em',
              fontWeight: '500',
              marginTop: 3.5,
              opacity: 0.5,
            }}
          >
            옵션목록({record?.product_option?.length})
          </p>
          <ul style={{ fontSize: '0.9em' }}>
            {record?.product_option?.map((item) => (
              <li>
                - {item?.option_title} /{' '}
                {item?.option?.map((option) => (
                  <>{option?.option_name || '-'}</>
                ))}{' '}
                /{' '}
                {stringToMoneyFormat(
                  item?.option?.reduce(
                    (acc, cur) => acc + cur?.option_price,
                    0,
                  ),
                  '원',
                )}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      title: '수량',
      key: 'num',
      dataIndex: 'num',
      width: 70,
      render: (v) => stringToMoneyFormat(v),
    },
    {
      title: '단가',
      key: 'price',
      dataIndex: 'price',
      width: 100,
      render: (v) => stringToMoneyFormat(v),
    },
    {
      title: '옵션금액',
      key: 'option_price',
      dataIndex: 'option_price',
      width: 100,
      render: (_, record) =>
        stringToMoneyFormat(
          record?.product_option?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0,
          ),
        ),
    },
    {
      title: '상품합계금액',
      key: 'price',
      dataIndex: 'price',
      width: 100,
      render: (val, record) =>
        stringToMoneyFormat(
          val * record?.num +
            (record?.product_option?.reduce(
              (acc, cur) =>
                acc +
                cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
              0,
            ) || 0),
        ),
    },
    {
      title: '포장방식',
      key: 'packaging_method',
      dataIndex: 'packaging_method',
      width: 100,
      render: (v) => PACKAGING_METHOD[v]?.title,
    },
    {
      title: '판매자',
      key: 'shop_information_name',
      dataIndex: 'shop_information_name',
      width: 200,
      render: (v, record) => record?.shop_information_name || '-',
    },

    // {
    //   title: '포장방식',
    //   key: 'packaging_method',
    //   dataIndex: 'packaging_method',
    //   // width: '17.5%',
    //   colSpan: 8,
    //   render: (v) => PACKAGING_METHOD[v].title,
    // },
  ];

  const deliveryAddressColumns = [
    /* === Line 1 === */
    {
      key: '주문자',
      label: '주문자',
      children: (
        <span>
          <a
            href={`/#/users/list?modal=${encodeURIComponent(
              encrypt(orderDetail?.user?.user_id),
            )}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(
                e.currentTarget.href,
                '_blank',
                'width=860, height=620',
              );
            }}
          >
            {orderDetail?.user?.user_name}
          </a>
        </span>
      ),
    },
    {
      key: 'user_phone',
      label: '주문자 연락처',
      children: <span>{orderDetail?.user?.user_phone}</span>,
    },

    /* === Line 2 === */
    ...(orderDetail?.type !== 'STORE_PICKUP'
      ? [
          {
            key: 'recipient_name',
            label: '수령인',
            children:
              editMode === 'DELIVERY' ? (
                <Input
                  size="small"
                  value={editData.recipient_name}
                  onChange={(e) =>
                    handleChange('recipient_name', e.target.value)
                  }
                />
              ) : (
                <span>
                  {orderDetail?.shop_order?.delivery_address?.recipient_name}
                </span>
              ),
          },
          {
            key: 'recipient_phone',
            label: '수령인 연락처',
            children:
              editMode === 'DELIVERY' ? (
                <Input
                  size="small"
                  value={editData.recipient_phone}
                  onChange={(e) =>
                    handleChange(
                      'recipient_phone',
                      Formatter.hyphen(e.target.value),
                    )
                  }
                />
              ) : (
                <span>
                  {orderDetail?.shop_order?.delivery_address?.recipient_phone}
                </span>
              ),
          },
        ]
      : []),

    /* === Line 3 === */
    {
      key: 'delivery_address.request',
      label: '배송요청사항',
      span: 2,
      children:
        editMode === 'DELIVERY' ? (
          <Input
            size="small"
            value={editData.request}
            onChange={(e) => handleChange('request', e.target.value)}
          />
        ) : (
          <span>{orderDetail?.shop_order?.delivery_address?.request}</span>
        ),
    },

    /* === Line 4 === */
    ...(orderDetail?.type !== 'STORE_PICKUP'
      ? [
          {
            key: 'recipient_address',
            label: '주소(상세)',
            span: 2,
            children:
              editMode === 'DELIVERY' ? (
                <Flex vertical gap={4}>
                  <Flex gap={4} align="center">
                    <Input
                      size="small"
                      value={`[${editData.recipient_postcode}] ${editData.recipient_address}`}
                      readOnly
                    />
                    <Button
                      size="small"
                      onClick={() => handleOpenAddressModal('delivery')}
                    >
                      주소 검색
                    </Button>
                  </Flex>
                  <Input
                    size="small"
                    value={editData.recipient_address_detail}
                    onChange={(e) =>
                      handleChange('recipient_address_detail', e.target.value)
                    }
                  />
                </Flex>
              ) : (
                <span>
                  [
                  {
                    orderDetail?.shop_order?.delivery_address
                      ?.recipient_postcode
                  }
                  ]{' '}
                  {orderDetail?.shop_order?.delivery_address?.recipient_address}
                  (
                  {
                    orderDetail?.shop_order?.delivery_address
                      ?.recipient_address_detail
                  }
                  )
                </span>
              ),
          },
        ]
      : [
          {
            key: 'recipient_address',
            label: '픽업지',
            span: 2,
            children:
              editMode === 'DELIVERY' ? (
                <Flex vertical gap={4}>
                  <Flex gap={4} align="center">
                    <Input
                      size="small"
                      value={`[${editData.forwarding_postcode}] ${editData.forwarding_address}`}
                      readOnly
                    />
                    <Button
                      size="small"
                      onClick={() => handleOpenAddressModal('pickup')}
                    >
                      주소 검색
                    </Button>
                  </Flex>
                  <Input
                    size="small"
                    value={editData.forwarding_address_detail}
                    onChange={(e) =>
                      handleChange('forwarding_address_detail', e.target.value)
                    }
                  />
                </Flex>
              ) : (
                <span>
                  [
                  {
                    orderDetail?.shop_order?.delivery_address
                      ?.forwarding_postcode
                  }
                  ]{' '}
                  {
                    orderDetail?.shop_order?.delivery_address
                      ?.forwarding_address
                  }
                  (
                  {
                    orderDetail?.shop_order?.delivery_address
                      ?.forwarding_address_detail
                  }
                  )
                </span>
              ),
          },
        ]),
  ];

  /**--------------------
   * 택배사 정보 저장
   * --------------------
   */
  const handleSaveDeliveryInfo = async (regular_payment_id) => {
    const { delivery_company, invoice_number } =
      parcelInfoForm[regular_payment_id];

    if (!delivery_company) {
      MessageAlert.info('택배사를 선택해주세요.');
      return;
    }

    if (!invoice_number) {
      MessageAlert.info('운송장 번호를 입력해주세요.');
      return;
    }

    // setLoading(true);
    try {
      const { parcel_service_id } = orderDetail;

      const d = [
        {
          parcel_service_id,
          regular_payment_id,
          delivery_company,
          invoice_number,
        },
      ];
      const { status, message } = await updateParcelServiceStatusBulk(d);

      if (status !== 200) throw { message };

      onReload && onReload();

      setTimeout(() => {
        MessageAlert.success('택배정보가 저장되었습니다.');
        // onUpdateParcel(d);
        setLoading(false);
      }, 350);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 정기배송 일괄 수정
   * --------------------
   */
  const handleRegularBulkUpdateStatus = async (
    newValue,
    regular_payment_id,
    type,
  ) => {
    const { delivery_company, invoice_number } =
      parcelInfoForm[regular_payment_id];
    const { parcel_service_id } = orderDetail;
    // 택배/운송장 정보가 없을 경우 처리 불가 예외처리
    if (type === 'delivery_status' && (!delivery_company || !invoice_number)) {
      return MessageAlert.warning('택배/운송장 정보를 먼저 등록해주세요');
    }
    if (!regular_payment_id) {
      return MessageAlert.warning('결제 정보를 찾을 수 없습니다.');
    }
    //
    const values = {
      delivery_status: {
        message: '배송',
        title: DELIVERY_STATUS[newValue]?.title,
        data: {
          delivery_status: newValue,
          ...(newValue === 'DELIVERED' && {
            delivery_date: dayjs().unix(),
          }), // 배송중으로 바꿀땐 전송 X),
        },
      },
      payment_state: {
        message: '결제',
        title: PAYMENT_STATUS_CONSTANT[newValue]?.title,
        data: { [type]: newValue },
      },
      purchase_status: {
        message: '주문',
        title: PURCHASE_STATUS[newValue]?.title,
        data: { [type]: newValue },
      },
    };
    ///
    // 상태변경 확인 컨펌
    if (
      !window.confirm(
        `${values[type].message}상태를 [${values[type].title}]로 변경하시겠습니까?`,
      )
    ) {
      return;
    }

    // setLoading(true);

    try {
      // 입력데이터
      const newData = [
        {
          parcel_service_id,
          regular_payment_id, // null 인경우 일반상품
          ...values[type].data,
        },
      ];

      // API
      const { status, message } = await updateParcelServiceStatusBulk(newData);
      if (status !== 200) throw { message };

      setLoading(false);
      onReload && onReload();

      MessageAlert.info(
        `${values[type].message}상테가 [${values[type].title}]로 변경되었습니다.`,
      );
    } catch (e) {
      setLoading(false);
      console.log('[regular2Detail][handleRegularBulkUpdateStatus] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 환불
   * --------------------
   */
  const handleRefund = async () => {
    if (!window.confirm('환불하시겠습니까?')) {
      return;
    }
    setLoading(true);
    try {
      const {
        refund,
        product,
        parcel_service_id,
        type,
        user_id,
        payment_method,
      } = refundModal;
      const cancelSeq = refund.length || 1;
      const refundList = selectedRefundList.map((item) => {
        const { regular_payment_id, payment_info } = item;
        const refundId = refund?.length ? refund?.[0]?.refund_id : null;
        const trno = payment_info?.trno || payment_info?.tid;
        return {
          trno: trno,
          cancelTaxFreeAmount: product.tax_free_amount,
          cancelSeq: cancelSeq,
          parcel_service_id,
          product_type: type,
          refund_reason: refundReason,
          refund_amount: refundPrice,
          refund_num: product.num,
          is_coupon_refund: recoveryValue.is_coupon_refund,
          refund_point: recoveryValue.refund_point || 0,
          user_id: user_id,
          refund_id: refundId,
          regular_payment_id: regular_payment_id,
        };
      });
      for (const item of refundList) {
        const res = await onCancel(item, payment_method);

        if (res) {
          // TODO: 데이터 최신화
          // getOrder();
          onReload && onReload();
          setRefundModal(null);
        }
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };
  /**
   * 데이터 수정
   * @param {string} t
   * @param {any} d
   */
  const handleEditSubmit = async (t, d) => {
    if (!Alert.confirm('주문 정보를 수정하시겠습니까?')) return;
    const { shop_order_id, ...rest } = d;
    try {
      const func = updateShopOrder;
      const data = {
        shop_order_id,
        [t]: rest,
      };

      const { status, message } = await func(data);

      if (status !== 200) {
        MessageAlert.warning('주문 정보 변경에 실패하였습니다.');
        console.warn('[handleEditSubmit] Fail : ', message);
      }
      onReload && onReload();
      MessageAlert.success('주문 정보가 변경되었습니다.');
      setEditMode(false);
    } catch (err) {
      console.error('handleEditSubmit', err);
      MessageAlert.error('정보 변경 실패!');
    }
  };

  /**
   * 수령지 값 변경
   * --
   */
  const handleChange = (t, v) => {
    setEditData((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**--------------------
   * 다음 주소 검색 Popup Open
   * --------------------
   */
  const handleOpenAddressModal = (type) => {
    const key1 =
      type === 'delivery' ? 'recipient_postcode' : 'forwarding_postcode';
    const key2 =
      type === 'delivery' ? 'recipient_address' : 'forwarding_address';

    openAddressModal((res) => {
      handleChange(key1, res.zonecode);
      handleChange(key2, res.address);
    });
  };

  // console.log('selectedRefundList', selectedRefundList);
  // console.log('refundModal', refundModal);
  // /**--------------------
  //  * 입금 상태 변경
  //  * --------------------
  //  */
  // const handleChangeDepositStatus = async () => {
  //   const confirm = window.confirm(
  //     `입금상태를 "입금확인" 상태로 변경하시겠습니까?`
  //   );

  //   if (!confirm) return;

  //   try {
  //     setLoading(true);

  //     // const parcelData = orderDetail?.parcel_service?.filter(
  //     //   (item) => item.deposit_status === 'PENDING'
  //     // );

  //     // if (!parcelData.length) {
  //     //   MessageAlert.info('처리 건이 없습니다.');
  //     //   return;
  //     // }

  //     // const updateData = parcelData.map((item) => ({
  //     //   parcel_service_id: item?.parcel_service_id,
  //     //   deposit_status: 'COMPLETED',
  //     // }));

  //     const updateData = [
  //       {
  //         parcel_service_id: orderDetail?.parcel_service_id,
  //         deposit_status: 'COMPLETED',
  //       },
  //     ];
  //     const { status } = await bulkUpdateParcelService(updateData);

  //     if (status !== 200) {
  //       MessageAlert.warning('입금상태 변경에 실패하였습니다.');
  //       return;
  //     }

  //     MessageAlert.success('변경되었습니다.');
  //     // getOrder();
  //   } catch (err) {
  //     console.error('[handleChangeDepositStatus] Error!! ', err);
  //     MessageAlert.error('입금상태 변경에 실패하였습니다.');
  //   } finally {
  //     setTimeout(() => {
  //       setLoading(false);
  //     }, 500);
  //   }
  // };

  const handleChangeDelivery = (t, v, regular_payment_id) => {
    const data = parcelInfoForm[regular_payment_id];

    const newData = {
      ...parcelInfoForm,
      [regular_payment_id]: {
        ...data,
        [t]: v,
      },
    };
    setParcelInfoForm(newData);
    // setParcelInfoForm;
  };

  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */

  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        {orderDetail && (
          <div>
            {/* ===== 상품정보: start ===== */}
            <Title size={5}>상품내역</Title>
            <div style={{ maxHeight: 420, overflowY: 'scroll' }}>
              <Table
                size="small"
                dataSource={[orderDetail?.product]}
                pagination={false}
                scroll={{ y: 680, x: 800 }} // 테이블 자체 스크롤 설정
                style={{
                  marginTop: 5,
                  borderTop: '2px solid #d1d1d1',
                  borderRadius: 5,
                }}
                // isChecked
                bordered
                columns={productColumns}
              />
            </div>
            {/* ===== 상품정보: end ===== */}
            <br />
            <br />
            {/* ===== 주문&배송정보: start ===== */}
            <Flex justify="space-between">
              <Title size={5}>주문정보</Title>
              {editMode === 'DELIVERY' ? (
                <Flex gap={4}>
                  <Button size="small" onClick={() => setEditMode(null)}>
                    취소
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      handleEditSubmit('delivery_address', editData)
                    }
                  >
                    완료
                  </Button>
                </Flex>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setEditMode('DELIVERY')}
                >
                  수정
                </Button>
              )}
            </Flex>
            <Descriptions
              size="small"
              bordered
              column={2}
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={deliveryAddressColumns}
            />
            <br />
            <br />
            {type === 'shipping' ? (
              <>
                <Title size={5}>배송정보</Title>

                <Table
                  size="small"
                  dataSource={orderDetail?.regular_payment_info}
                  columns={deliveryListColumns}
                  scroll={{ x: 'max-content' }}
                  style={{
                    borderTop: '2px solid #d1d1d1',
                    borderRadius: 5,
                    marginTop: 5,
                  }}
                />

                <br />
                <br />
              </>
            ) : null}
            {/* ===== 주문&배송정보: end ===== */}

            {/* ===== 결제정보: start ===== */}
            <Title size={5}>결제정보</Title>
            <Table
              size="middle"
              dataSource={orderDetail?.regular_payment_info}
              columns={paymentListColumns(orderDetail)}
              scroll={{ x: 'max-content' }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
            />

            <Flex
              align="center"
              justify="flex-end"
              gap={5}
              style={{ marginTop: 5 }}
            >
              {/* {modalType === 'order' && type ? (
                <Button onClick={() => handleChangeDepositStatus()}>
                  입금확인
                </Button>
              ) : null} */}
              <Button
                color="danger"
                variant="outlined"
                onClick={() => {
                  setRefundModal(orderDetail);
                }}
              >
                주문취소
              </Button>
            </Flex>
            {/* ===== 결제정보: end ===== */}
          </div>
        )}
      </Spin>
      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        title="주문취소"
        width={1500}
        open={refundModal !== null}
        closable={true}
        onCancel={() => {
          setRefundModal(null);
          setSelectedRefundList([]);
          setRefundPrice(0);
        }}
        footer={
          <>
            <Button
              color="danger"
              variant="outlined"
              onClick={handleRefund}
              disabled={selectedRefundList?.length === 0}
            >
              주문취소
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '12px 0' }} />
        {refundModal && (
          <>
            <p>환불 정보</p>
            <Table
              size="small"
              dataSource={refundModal?.refund}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              columns={refundListColumns(refundModal)}
              pagination={false}
              scroll={{ y: 480, x: 'max-content' }} // 테이블 자체 스크롤 설정
            ></Table>
            <br />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p>취소항목을 선택해주세요</p>
              <p>[{selectedRefundList.length}]개 상품 선택</p>
            </div>
            <Table
              size="small"
              dataSource={refundModal?.regular_payment_info?.map((item) => {
                const shop_order = refundModal?.shop_order;
                const totalOrderPrice = shop_order?.product_information?.reduce(
                  (totalPrice, product) => {
                    const productPrice =
                      product.price * product.num +
                      (product?.product_option?.reduce(
                        (acc, cur) =>
                          acc +
                          cur?.option?.reduce(
                            (acc, cur) => acc + (cur?.option_price || 0),
                            0,
                          ),
                        0,
                      ) ?? 0);
                    return totalPrice + productPrice;
                  },
                  0,
                );

                return {
                  ...item,
                  key: item.regular_payment_id,
                  final_delivery_price: shop_order.final_delivery_price,
                  total_order_price:
                    totalOrderPrice +
                    shop_order.final_delivery_price -
                    refundModal.refunded_amount,
                };
              })}
              pagination={false}
              scroll={{ x: 'max-content' }} // 테이블 자체 스크롤 설정
              style={{
                marginTop: 5,
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
              }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedRefundList?.map((item) => item.key),

                onChange: (_, row) => {
                  setSelectedRefundList(row);
                  setRefundPrice(row[0]?.price);
                  setRefundReason('');
                  setRecoveryValue((prev) => ({
                    ...prev,
                    is_coupon_refund: 'N',
                    refund_point: 0,
                  }));
                },
                getCheckboxProps: () => ({
                  disabled: refundModal?.refund?.some(
                    (r) => r?.refund_status === 'COMPLETED',
                  ),
                }),
              }}
              // isChecked
              bordered
              columns={paymentListColumns(refundModal)}
            />
            <br />
            <Flex vertical gap={10}>
              <Title size={5}>취소정보</Title>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>환불금액: </p>
                <InputNumber
                  placeholder="환불 금액을 입력해주세요"
                  min={0}
                  max={selectedRefundList.reduce(
                    (acc, cur) => (acc += cur.total_order_price),
                    0,
                  )}
                  value={refundPrice}
                  onChange={(e) => setRefundPrice(e)}
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>환불사유: </p>
                <Input.TextArea
                  placeholder="텍스트를 입력해주세요"
                  rows={2}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>복구포인트: </p>
                <InputNumber
                  placeholder="복구될 포인트를 입력하세요"
                  min={0}
                  value={recoveryValue?.refund_point}
                  onChange={(e) =>
                    setRecoveryValue((prev) => ({
                      ...prev,
                      refund_point: e,
                    }))
                  }
                  disabled={selectedRefundList.length <= 0}
                  style={{ width: '80%' }}
                />
              </Flex>
              <Flex gap={3} style={{ alignItems: 'center' }}>
                <p>쿠폰복구여부: </p>
                <Radio.Group
                  disabled={selectedRefundList.length <= 0}
                  value={recoveryValue.is_coupon_refund}
                  onChange={(e) => {
                    setRecoveryValue((prev) => ({
                      ...prev,
                      is_coupon_refund: e.target.value,
                    }));
                  }}
                >
                  <Radio value="Y">쿠폰복구</Radio>
                  <Radio value="N">쿠폰복구안함</Radio>
                </Radio.Group>
              </Flex>
            </Flex>
          </>
        )}
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}
    </>
  ); // JSX: end
};

RegularDetail.defaultProps = {
  orderId: null,
  isModalVisible: null,
  orderDetail: {},
  onLoaded: () => {},
  // onUpdateParcel: () => {},
};
export default RegularDetail;
